import { type FC, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import ArrowUp from '@assets/img/icons/arrow-top.svg?react';
import CheckIcon from '@assets/img/icons/check-2.svg?react';
import DoneIcon from '@assets/img/icons/check-black.svg?react';
import cn from 'classnames';
import { motion } from 'framer-motion';

import { getIcon } from '@/common/lib/getIcon';
import { type ProductOfDayDataType } from '@/common/types/contract';
import { useAppSelector } from '@/redux/store';

import ContractBackground from '../common/ContractBackground/ContractBackground';
import { ModalContext } from '../Modals/Modal/config';
import { StringXSmall } from '../Typography/Typography';
import { Picture } from '../ui/Picture/Picture';

import css from './styles.module.scss';

import lock from '/img/icons/lock.svg';

interface ProductOfDayItemProps {
  product: ProductOfDayDataType;
  isUninitialized?: boolean;
}

const ProductOfDayItem: FC<ProductOfDayItemProps> = ({
  product,
  isUninitialized,
}) => {
  const image = getIcon(product.name);
  const userLevel = useAppSelector((state) => state.user.user?.level || 0);
  const { t } = useTranslation();
  const validLevel =
    product.id !== '-' &&
    (!product.availableLevel || product.availableLevel <= userLevel);
  const { openModal } = useContext(ModalContext);

  const handleClick = (): void => {
    if (!validLevel) return;

    openModal({
      renderKind: 'productOfDayModal',
      props: { productId: product.id },
    });
  };

  const isCompleted = product.progress >= product.countToReward;
  const isDone = !!product.chestBoxOpenedAt;

  return (
    <div className="relative size-full" role="button" onClick={handleClick}>
      <div
        className={cn(
          'size-full relative bg-white/10 rounded-lg overflow-hidden flex flex-col items-center py-2 z-0',
          validLevel ? 'justify-end gap-1' : 'justify-center gap-2',
        )}
      >
        <ContractBackground
          type={product.type === 'premium' ? 'purple' : 'green'}
          disabled={!validLevel}
        />
        {validLevel ? (
          <>
            <Picture
              className="size-full absolute"
              img={image?.png}
              webp={image?.webp}
              alt="pruduct icon"
            />

            <div className="p-1 bg-[#1D1F25]/60 gap-0.5 flex items-center backdrop-blur-sm rounded-md text-xs font-bold">
              {isDone ? (
                <>
                  {t('Done')}
                  <DoneIcon
                    style={{ width: 12, height: 12, marginLeft: '2px' }}
                  />
                </>
              ) : (
                <>
                  <span>{product.progress}</span>
                  <span>/</span>
                  <span>{product.countToReward}</span>
                </>
              )}
            </div>

            <div className="w-16 relative rounded-lg h-1.5 bg-main-bg overflow-hidden flex">
              <div
                className="bg-[#82E024] rounded-[0.25rem] h-full"
                style={{
                  width: (product.progress / product.countToReward) * 100 + '%',
                }}
              />
            </div>
          </>
        ) : (
          <>
            <img src={lock} alt="lock" className="size-6" />
            <StringXSmall weight={'medium'} className="text-white/60">
              {t('Available Level')} {product.availableLevel}
            </StringXSmall>
          </>
        )}
      </div>
      {product.extraStarPoints && validLevel && (
        <motion.div
          initial={{ opacity: 0, scale: 1.5, x: '-50%', y: '-50%' }}
          animate={{ opacity: 1, scale: 1, x: '-50%', y: '-50%' }}
          transition={{ delay: isUninitialized ? 0.6 : 0.8, duration: 0.5 }}
          className={css.level}
        >
          <StringXSmall weight="bold" className="!text-black">
            {product.extraStarPoints}
          </StringXSmall>
        </motion.div>
      )}
      {isCompleted && !isDone && (
        <div className={css.check}>
          <CheckIcon className="w-[20px] h-[15px]" />
        </div>
      )}
      {product.type !== 'premium' && validLevel && !isDone && !isCompleted && (
        <div className="absolute top-0 right-0 flex items-center justify-center size-6">
          <ArrowUp className="text-gold" />
        </div>
      )}
    </div>
  );
};

export default ProductOfDayItem;
